import React from 'react';
import { ArrowLeft, UtensilsCrossed, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { restaurantData } from '../../mockDemos';
import { useNavigate } from 'react-router-dom';

export const RestaurantDemo = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <UtensilsCrossed className="text-orange-500" size={32} />
              <span className="text-2xl font-bold text-gray-900">{restaurantData.name}</span>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-orange-500 text-orange-500 hover:bg-orange-50"
            >
              <ArrowLeft className="mr-2" size={16} />
              Back to Portfolio
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-orange-50 via-amber-50 to-white">
        <div className="max-w-7xl mx-auto text-center space-y-6">
          <Badge className="bg-orange-500 text-white hover:bg-orange-600">
            Demo Website by Pranshik
          </Badge>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900">
            {restaurantData.hero.headline}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {restaurantData.hero.subtext}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white">
              Order Online
            </Button>
            <Button size="lg" variant="outline" className="border-orange-500 text-orange-500 hover:bg-orange-50">
              Book a Table
            </Button>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Our Menu</h2>
            <p className="text-xl text-gray-600">Discover our culinary delights</p>
          </div>
          
          <Tabs defaultValue="Starters" className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-4 mb-8">
              {restaurantData.menu.map((category) => (
                <TabsTrigger key={category.category} value={category.category}>
                  {category.category}
                </TabsTrigger>
              ))}
            </TabsList>
            
            {restaurantData.menu.map((category) => (
              <TabsContent key={category.category} value={category.category}>
                <div className="grid md:grid-cols-2 gap-6">
                  {category.items.map((item) => (
                    <Card key={item.id} className="border-2 hover:border-orange-500 transition-all duration-300">
                      <CardHeader>
                        <div className="flex justify-between items-start">
                          <div>
                            <CardTitle className="text-xl">{item.name}</CardTitle>
                            <CardDescription className="mt-2">{item.description}</CardDescription>
                          </div>
                          {item.vegetarian && (
                            <Badge variant="outline" className="text-green-600 border-green-200">
                              Veg
                            </Badge>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="flex justify-between items-center">
                          <span className="text-2xl font-bold text-orange-500">{item.price}</span>
                          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                            Add to Cart
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </TabsContent>
            ))}
          </Tabs>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-4 mb-12">
            <h2 className="text-4xl font-bold text-gray-900">Our Specialties</h2>
            <p className="text-xl text-gray-600">Cuisines we master</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {restaurantData.specialties.map((specialty) => (
              <Card key={specialty.id} className="text-center border-2 hover:border-orange-500 transition-all duration-300">
                <CardHeader>
                  <div className="text-6xl mb-4">{specialty.icon}</div>
                  <CardTitle className="text-2xl">{specialty.name}</CardTitle>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hours Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Card className="border-2 border-orange-500">
            <CardHeader className="text-center">
              <Clock className="w-16 h-16 mx-auto mb-4 text-orange-500" />
              <CardTitle className="text-3xl">Opening Hours</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <div>
                <p className="text-lg font-semibold text-gray-900">Monday - Friday</p>
                <p className="text-gray-600">{restaurantData.hours.weekdays}</p>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-900">Saturday - Sunday</p>
                <p className="text-gray-600">{restaurantData.hours.weekends}</p>
              </div>
              <Button className="mt-6 bg-orange-500 hover:bg-orange-600 text-white">
                Reserve Your Table
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto text-center space-y-4">
          <div className="flex items-center justify-center space-x-3">
            <UtensilsCrossed size={28} />
            <span className="text-2xl font-bold">{restaurantData.name}</span>
          </div>
          <p className="text-gray-400">{restaurantData.tagline}</p>
          <div className="pt-4 border-t border-gray-800">
            <p className="text-sm text-gray-500">
              This is a demo website created by <span className="text-orange-500 font-semibold">Pranshik</span>
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Want a website like this? <a href="/" className="text-orange-500 hover:underline">Contact me</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
